import React, {
  useContext,
  useEffect,
  useState,
  Dispatch,
  useCallback,
  useMemo,
  useRef,
} from 'react';

import { AuthCheckText } from '@app/components';
import { useToken } from '@app/hooks';

import { useBalance } from '@app/hooks/user/mybalance.tsx';
import TPSLEDIT from '@app/organisms/Trade/TradeDetail/TPSLEDIT.tsx';
import { IconShare2 } from '@tabler/icons-react';
import { HiOutlineShare } from 'react-icons/hi2';
import { HiMiniShare } from 'react-icons/hi2';
import { Button, message, Modal, Skeleton, Table, Tooltip } from 'antd';
import BigNumber from 'bignumber.js';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { View } from '../../../../../styles/reactnative';
import { ThemeContext } from '../../../../../theme/ThemeProvider';
import NoData from '../../../../components/Common/CFlatList/NoData';
import CRenderEmpty from '../../../../components/CRenderEmpty';
import KrwPrice from '../../../../components/KrwPrice';
import { COLORS } from '../../../../constants';
import { SETTING_TYPE_SYSTEM } from '../../../../constants/SETTING';
import { useExchangeStore } from '../../../../hooks/trade/exchangeStore';
import { useIndexPrice } from '../../../../hooks/trade/indexprice';
import { useTradeModalStore } from '../../../../hooks/trade/modalStore';
import { useTradePosition } from '../../../../hooks/trade/tradePosition';
import useTradeStore from '../../../../hooks/trade/tradeStore';
import { getSetting, useSetting } from '../../../../hooks/user/mysetting';
import ShareModal from '../../../ShareModal';
import useCloseAllStore from '../closeAllStore';
import ContractsColumn from '../Columns/ContractsColumn';
import Closedby from './closedBy';
import { TradeDetailListSection, TradeText } from './styles';
import AddMargin from './AddMargin';
import API from '@/utils/api';
import throttle from 'lodash/throttle';
import useGetCoinTier from '@app/hooks/trade/coinTier';
import { formatKNumber } from '@utils/formatKNumber.tsx';
import { formatDecimal4Truncate } from '@/utils/formatDecimal';
import { pnlCalculate } from '@utils/pnlCalculate.tsx';
import { useMultiMarkPriceStore } from '@app/hooks/multiMarkPriceStore.tsx';
import TradeMarginRatio from './TradeMarginRatio';
import EmptyMessage from '@/app/components/EmptyMessage';
import useScrollLayout from '../useScrollLayout';
import EmptyPosition from '@/app/components/EmptyPosition';

// 커스텀 Sorter 아이콘 - 컴포넌트 외부에 정의하여 리렌더링 방지
const SorterIcon = React.memo(
  ({
    active = false,
    direction = null,
  }: {
    active?: boolean;
    direction?: 'ascend' | 'descend' | null;
  }) => {
    const activeColor = '#07D993';
    const inactiveColor = '#5B5D6B';

    const upColor =
      active && direction === 'ascend' ? activeColor : inactiveColor;
    const downColor =
      active && direction === 'descend' ? activeColor : inactiveColor;

    return (
      <svg
        width="7"
        height="12"
        viewBox="0 0 7 12"
        fill="none"
        style={{ marginLeft: 2 }}
      >
        <path
          d="M2.68077 1.17033C3.07887 0.601616 3.92113 0.601614 4.31923 1.17033L5.89858 3.42654C6.36252 4.08931 5.88837 5 5.07934 5H1.92066C1.11163 5 0.637479 4.08932 1.10142 3.42654L2.68077 1.17033Z"
          fill={upColor}
        />
        <path
          d="M4.31923 10.8297C3.92113 11.3984 3.07887 11.3984 2.68077 10.8297L1.10142 8.57346C0.63748 7.91069 1.11163 7 1.92066 7H5.07934C5.88837 7 6.36252 7.91068 5.89858 8.57346L4.31923 10.8297Z"
          fill={downColor}
        />
      </svg>
    );
  }
);

// 커스텀 Column Header - 컴포넌트 외부에 정의
const ColumnHeader = React.memo(
  ({
    title,
    columnKey,
    hasSorter = true,
    sortedInfo,
  }: {
    title: string;
    columnKey?: string;
    hasSorter?: boolean;
    sortedInfo: {
      columnKey: string | null;
      order: 'ascend' | 'descend' | null;
    };
  }) => {
    const isActive = sortedInfo.columnKey === columnKey;
    const direction = isActive ? sortedInfo.order : null;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <span
          style={{
            fontFamily: 'NanumSquareRound',
            fontSize: 12,
            color: '#b3b5bd',
            letterSpacing: '-0.24px',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </span>
        {hasSorter && <SorterIcon active={isActive} direction={direction} />}
      </div>
    );
  }
);

interface TradePositionsTableProps {
  item: any;
  amount: any;
  setAmount: Dispatch<any>;
  onClose?: () => void;
}

const TradePositionsTable = ({
  parentHeight = 300,
  type = '',
  orderBookValue,
  isMobile = false,
}: any) => {
  const { data: token } = useToken();
  const { textw } = useContext(ThemeContext).theme.COLORS;
  const { t } = useTranslation();

  const [shareData, setShareData] = React.useState<any>(null);

  const [marginModal, setMarginModal] = React.useState<any>(null);
  const [reduceMode, setReduceMode] = React.useState(false);
  const [maxReduceAmount, setMaxReduceAmount] = React.useState(0);

  const [closeAll, setCloseAll] = React.useState(false);

  const closed = useCloseAllStore((state) => state.closed);
  const toggleClosed = useCloseAllStore((state) => state.toggleClosed);

  const [refresh, setRefresh] = React.useState(false);
  const [forceUpdate, setForceUpdate] = React.useState(0);

  // 테이블 정렬 상태 관리
  const [sortedInfo, setSortedInfo] = React.useState<{
    columnKey: string | null;
    order: 'ascend' | 'descend' | null;
  }>({ columnKey: null, order: null });

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setForceUpdate((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  const { mutate: mutateModalStore, data: modalStore } = useTradeModalStore();

  const { data: tradeStore } = useTradeStore();
  const orderType = tradeStore?.main_sub || 'M';

  const { data: setting, updateSetting } = useSetting(SETTING_TYPE_SYSTEM);
  const equivshow = getSetting(setting, 'EQUIVSHO', 1) == 1 ? true : false;

  const { getPrice } = useIndexPrice(tradeStore?.ep_id || 0);
  const { EPs } = useExchangeStore();
  const {
    data: pos,
    mutate,
    isLoading: isPositionLoading,
  } = useTradePosition();

  const currency = tradeStore?.pairs?.split('/')[1];
  const { data: balance } = useBalance(currency, 1);

  const [amount, setAmount] = useState<any>(0);

  const open_pos = pos || [];

  let pnl = 0;
  for (let i = 0; i < open_pos?.length; i++) {
    if (open_pos[i].cross_iso == 'I') continue;

    if (open_pos[i].long_short === 'L') {
      pnl +=
        (getPrice(open_pos?.[i]?.exchange_pairs_id) -
          open_pos?.[i]?.open_price) *
        open_pos[i]?.amount *
        open_pos[i]?.leverage;
    } else {
      pnl +=
        (open_pos[i]?.open_price - getPrice(open_pos?.[i]?.exchange_pairs_id)) *
        open_pos[i]?.amount *
        open_pos[i]?.leverage;
    }
  }

  let list = pos;

  if (modalStore?.iscurrent) {
    list = pos?.filter(
      (item: any) => item?.exchange_pairs_id == tradeStore?.ep_id
    );
  } else {
    list = pos
      ? [...pos].sort((a, b) => {
          const isA = a?.exchange_pairs_id == tradeStore?.ep_id ? 1 : 0;
          const isB = b?.exchange_pairs_id == tradeStore?.ep_id ? 1 : 0;

          if (isA !== isB) return isB - isA;

          const aValue = BigNumber(a?.amount)
            .multipliedBy(getPrice(a?.exchange_pairs_id))
            .multipliedBy(a?.leverage)
            .toNumber();

          const bValue = BigNumber(b?.amount)
            .multipliedBy(getPrice(b?.exchange_pairs_id))
            .multipliedBy(b?.leverage)
            .toNumber();

          return bValue - aValue; // 내림차순
        })
      : [];
  }

  const { needsScroll, headerHeight } = useScrollLayout(
    containerRef,
    list,
    parentHeight
  );

  useEffect(() => {
    setRefresh(true);
    setTimeout(() => {
      setRefresh(false);
    }, 100);
  }, [list?.length]);

  const throttledGetPrice = throttle((id: number) => {
    return getPrice(id);
  }, 1000);

  // 정렬에 사용할 유틸 함수들
  const getEp = (id: any) => EPs?.find((ep: any) => +ep.id === +id);
  const getPairs = (item: any) => getEp(item?.exchange_pairs_id)?.pairs || '';
  const getSymbolLower = (item: any) =>
    getPairs(item).replace('/', '').toLowerCase();
  const currentPriceOf = (item: any) =>
    Number(getPrice(item?.exchange_pairs_id) || 0);

  const qtyValueOf = (item: any) => {
    const curr = currentPriceOf(item);
    if (orderType === 'M')
      return BigNumber(item?.amount).multipliedBy(item?.leverage).toNumber();
    return BigNumber(item?.amount)
      .multipliedBy(curr)
      .multipliedBy(item?.leverage)
      .toNumber();
  };

  const entryPriceOf = (item: any) => Number(item?.price) || 0;
  const liqPriceOf = (item: any) => Number(item?.liquid_price) || 0;

  const { getCoinTier } = useGetCoinTier();

  const getUnrealizedPnl = (
    item: any,
    currentPrice: number,
    amountWithLev: number
  ) => {
    if (item?.long_short === 'L') {
      return BigNumber(currentPrice)
        .minus(item?.price)
        .multipliedBy(amountWithLev)
        .toNumber();
    } else {
      return BigNumber(item?.price)
        .minus(currentPrice)
        .multipliedBy(amountWithLev)
        .toNumber();
    }
  };

  const getTotalDeposit = (
    openPositions: any[],
    getPriceFn: (id: number) => number,
    walletBalance: any
  ) => {
    let pnl = 0;
    for (let i = 0; i < openPositions?.length; i++) {
      if (openPositions[i].cross_iso == 'I') continue;
      if (openPositions[i].long_short === 'L') {
        pnl +=
          (getPriceFn(openPositions?.[i]?.exchange_pairs_id) -
            openPositions?.[i]?.open_price) *
          openPositions[i]?.amount *
          openPositions[i]?.leverage;
      } else {
        pnl +=
          (openPositions[i]?.open_price -
            getPriceFn(openPositions?.[i]?.exchange_pairs_id)) *
          openPositions[i]?.amount *
          openPositions[i]?.leverage;
      }
    }
    const base = BigNumber(walletBalance?.balance).plus(pnl).toNumber();
    return isNaN(base) ? 0 : base;
  };

  const marginRatioOf = (item: any) => {
    const isCross = item?.cross_iso === 'C';
    if (isCross) {
      // 전체 유지증거금
      const sumMm = (pos || [])?.reduce((acc: number, curr: any) => {
        const ep = getEp(curr?.exchange_pairs_id);
        const currentPrice = currentPriceOf(curr);
        const coin = ep?.pairs?.split('/')[0];
        const amountUsdt =
          Number(curr?.amount) * Number(curr?.leverage) * currentPrice;
        const { mmr, ma } = getCoinTier(coin, amountUsdt);
        const maintenanceMargin =
          Number(curr?.amount) *
            Number(curr?.leverage) *
            Number(curr?.price) *
            mmr -
          ma;
        return acc + (maintenanceMargin || 0);
      }, 0);
      const totalDeposit = getTotalDeposit(pos || [], getPrice, balance);
      return (sumMm / (totalDeposit || 1)) * 100;
    } else {
      const ep = getEp(item?.exchange_pairs_id);
      const currentPrice = currentPriceOf(item);
      const coin = ep?.pairs?.split('/')[0];
      const amountUsdt =
        Number(item?.amount) * Number(item?.leverage) * currentPrice;
      const { mmr, ma } = getCoinTier(coin, amountUsdt);
      const maintenanceMargin =
        Number(item?.amount) *
          Number(item?.leverage) *
          Number(item?.price) *
          mmr -
        ma;
      const amountWithLev = Number(item?.amount) * Number(item?.leverage);
      const unrealized = getUnrealizedPnl(item, currentPrice, amountWithLev);
      const marginBalance = Number(item?.margin || 0) + unrealized;
      return (maintenanceMargin / (marginBalance || 1)) * 100;
    }
  };

  const unrealizedPnlOf = (item: any) => {
    const currentPrice = currentPriceOf(item);
    const amountWithLev = Number(item?.amount) * Number(item?.leverage);
    let pnl = 0;
    if (item?.long_short === 'L') {
      pnl = BigNumber(currentPrice)
        .minus(item?.price)
        .multipliedBy(amountWithLev)
        .toNumber();
    } else {
      pnl = BigNumber(item?.price)
        .minus(currentPrice)
        .multipliedBy(amountWithLev)
        .toNumber();
    }
    return pnl;
  };

  const realizedPnlWithFeesOf = (item: any) => {
    // orders 배열이 시간 역순(최신→과거)이므로 open_close로 명시적 구분 필요
    const entryOrders =
      item?.orders?.filter((o: any) => o.open_close === 'O') || [];
    const closeOrders =
      item?.orders?.filter((o: any) => o.open_close === 'C') || [];

    // 진입 수수료: 모든 진입 주문의 수수료 합산 (이미 지불된 비용)
    const entryFee = entryOrders.reduce(
      (sum: number, o: any) => sum + Number(o.fee_price || 0),
      0
    );

    // 청산 수수료: 모든 청산 주문의 수수료 합산
    const exitFees = closeOrders.reduce(
      (sum: number, o: any) => sum + Number(o.fee_price || 0),
      0
    );

    let realizedPnLWithFees = 0;

    if (closeOrders.length === 0) {
      // 청산 없음 → 진입 수수료만 실현 (이미 지불됨)
      realizedPnLWithFees = -entryFee;
    } else {
      // 청산 있음 → pnl_price - 전체 진입 수수료 - 청산 수수료
      // 진입 수수료는 포지션 오픈 시 이미 전액 지불되었으므로 비율 계산 없이 전체 포함
      realizedPnLWithFees = BigNumber(item.pnl_price || 0)
        .minus(entryFee)
        .minus(exitFees)
        .toNumber();
    }

    return realizedPnLWithFees;
  };

  const handleOk = () => {
    if (amount == 0) {
      message.error(t('pleaseEnterTheMargin'));
      return;
    }

    // 감소 모드일 때는 음수값만 허용
    if (amount > 0 && reduceMode) {
      message.error(t('reduceModeMustBeNegative'));
      return;
    }

    // 증가 모드일 때는 양수값만 허용
    if (amount < 0 && !reduceMode) {
      message.error(t('addModeMustBePositive'));
      return;
    }

    // 감소 모드에서 최대 감소 가능 금액 초과 여부 확인
    if (reduceMode && Math.abs(amount) > maxReduceAmount) {
      message.error(t('exceededMaxReduceAmount'));
      return;
    }

    API.postNoLogout('/trade/margin', token, {
      position: marginModal?.id,
      amount: amount,
    })
      .then((res) => {
        if (res?.result?.success) {
          if (amount >= 0) {
            message.success(t('marginAdded'));
          } else {
            message.success(t('marginReduced'));
          }
          try {
            setTimeout(() => {
              mutate();
            }, 100);
          } catch (e) {
            console.log('POS', e);
          }
          setMarginModal(false);
        } else {
          if (res?.result?.message) {
            message.error(res?.result?.message);
          } else {
            message.error(t('failedToUpdateMargin'));
          }
        }
      })
      .catch((e) => {
        console.error('marginadd', e);
        message.error(t('failedToAddMargin'));
      });
  };

  const handleReduceModeChange = useCallback((isReduce) => {
    setReduceMode(isReduce);
    // 모드 전환시 입력값 초기화
    setAmount('');
  }, []);

  const handleMaxReduceAmountChange = useCallback((amount) => {
    setMaxReduceAmount(amount);
  }, []);

  const { connect } = useMultiMarkPriceStore();

  useEffect(() => {
    if (!EPs?.length || !list?.length) return;

    const symbols = list
      .map((item) => {
        const ep = EPs.find((ep) => +ep.id === +item.exchange_pairs_id);
        return ep?.pairs?.replace('/', '').toLowerCase();
      })
      .filter(Boolean);

    if (symbols.length) {
      connect([...new Set(symbols)] as string[]);
    }
  }, [list]);

  return (
    <TradeDetailListSection type={type} ref={containerRef}>
      <Modal
        footer={[
          <Button key="submit" type="primary" onClick={handleOk}>
            {t('confirm')}
          </Button>,
        ]}
        centered
        onCancel={() => {
          setMarginModal(false);
        }}
        title={t('updateMargin')}
        open={marginModal ? true : false}
      >
        <View>
          <AddMargin
            item={marginModal}
            amount={amount}
            setAmount={setAmount}
            onReduceModeChange={handleReduceModeChange}
            onMaxReduceAmountChange={handleMaxReduceAmountChange}
          />
        </View>
      </Modal>

      {list?.length ? (
        <StyledTableContainer>
          <Table
            showHeader={true}
            bordered={false}
            loading={refresh}
            showSorterTooltip={false}
            dataSource={refresh ? [] : list}
            rowKey={(record) => record?.id || record?.exchange_pairs_id}
            onChange={(pagination, filters, sorter: any) => {
              // 정렬 상태 업데이트
              setSortedInfo({
                columnKey: sorter.columnKey || null,
                order: sorter.order || null,
              });
            }}
            columns={
              type === 'orders'
                ? [
                    {
                      title: t('contracts') + '',
                      dataIndex: 'contracts',
                      align: 'center',
                      key: 'contracts',
                      width: 100,
                      sorter: (a: any, b: any) => {
                        const aPairs = getPairs(a);
                        const bPairs = getPairs(b);
                        return (aPairs || '').localeCompare(bPairs || '');
                      },
                      render: (text: any, item) => (
                        <div
                          style={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'flex-end',
                          }}
                        >
                          <ContractsColumn item={item} />
                        </div>
                      ),
                    },
                    {
                      title: t('qty') + '',
                      dataIndex: 'amount',
                      key: 'amount',
                      align: 'right',
                      sorter: (a: any, b: any) => qtyValueOf(a) - qtyValueOf(b),
                      render: (text: any, item: any) => {
                        const EP = EPs?.find(
                          (item2: any) => +item2.id == +item?.exchange_pairs_id
                        );
                        const coin = EP?.pairs?.split('/')[0];
                        const isLong = item?.long_short === 'L';

                        return (
                          <TradeText
                            style={{
                              letterSpacing: 0,
                              minWidth: 150,
                              whiteSpace: 'nowrap',
                              lineHeight: 'normal',
                              flexDirection: 'column',
                              alignContent: 'center',
                              alignItems: 'flex-end',
                              color: isLong
                                ? COLORS.lightGreen
                                : COLORS.lightRed,
                            }}
                          >
                            {orderType === 'M'
                              ? BigNumber(item?.amount)
                                  .multipliedBy(item?.leverage)
                                  .toFormat(EP?.dp || EP?.dp2 || 3)
                              : BigNumber(item?.amount)
                                  .multipliedBy(
                                    getPrice(item?.exchange_pairs_id)
                                  )
                                  .multipliedBy(item?.leverage)
                                  .toFormat(4)}{' '}
                            {orderType === 'M' ? coin : 'USDT'}
                          </TradeText>
                        );
                      },
                    },
                    {
                      title: t('avgEntryPrice') + '',
                      dataIndex: 'open_price',
                      key: 'open_price',
                      align: 'right',
                      sorter: (a: any, b: any) =>
                        entryPriceOf(a) - entryPriceOf(b),
                      render: (text: any, item) => {
                        const EP = EPs?.find(
                          (item2: any) => +item2.id == +item?.exchange_pairs_id
                        );

                        return (
                          <TradeText
                            style={{
                              letterSpacing: 0,
                              minWidth: 90,
                              lineHeight: 1,
                              flexDirection: 'column',
                              alignContent: 'center',
                              alignItems: 'flex-end',
                              justifyContent: 'flex-end',
                            }}
                          >
                            {isPositionLoading && !item?.price ? (
                              <Skeleton.Input
                                active
                                size="small"
                                style={{ width: 60, height: 14 }}
                              />
                            ) : item?.price ? (
                              BigNumber(item.price).toFormat(EP?.dp || 3)
                            ) : (
                              '--'
                            )}
                          </TradeText>
                        );
                      },
                    },
                    {
                      title: t('markPrice') + '',
                      dataIndex: 'mark_price',
                      key: 'mark_price',
                      align: 'right',
                      sorter: (a: any, b: any) =>
                        currentPriceOf(a) - currentPriceOf(b),
                      render: (text: any, item) => {
                        const EP = EPs?.find(
                          (item2: any) => +item2.id == +item?.exchange_pairs_id
                        );
                        return (
                          <TradeText
                            style={{
                              letterSpacing: 0,
                              minWidth: 90,
                              lineHeight: 1,
                              flexDirection: 'column',
                              alignContent: 'center',
                              alignItems: 'flex-end',
                              justifyContent: 'flex-end',
                            }}
                          >
                            {BigNumber(
                              throttledGetPrice(item?.exchange_pairs_id)
                            ).toFormat(EP?.dp || 3)}
                          </TradeText>
                        );
                      },
                    },
                    {
                      title: t('liqPrice') + '',
                      dataIndex: 'liquidation_price',
                      key: 'liquidation_price',
                      align: 'right',
                      width: 150,
                      sorter: (a: any, b: any) => liqPriceOf(a) - liqPriceOf(b),
                      render: (text: any, item) => {
                        const EP = EPs?.find(
                          (item2: any) => +item2.id == +item?.exchange_pairs_id
                        );
                        const liquidPrice = BigNumber(item?.liquid_price);
                        const displayValue = liquidPrice.isNegative()
                          ? '-'
                          : liquidPrice.toFormat(EP?.dp || 3);

                        if (liquidPrice.isNegative()) {
                          return (
                            <Tooltip title={t('positionRiskLowTooltip')}>
                              <TradeText
                                style={{
                                  letterSpacing: 0,
                                  minWidth: 90,
                                  lineHeight: 1,
                                  flexDirection: 'column',
                                  alignContent: 'center',
                                  alignItems: 'flex-end',
                                  justifyContent: 'flex-end',
                                  cursor: 'help',
                                }}
                              >
                                {displayValue}
                              </TradeText>
                            </Tooltip>
                          );
                        }

                        return (
                          <TradeText
                            style={{
                              letterSpacing: 0,
                              minWidth: 90,
                              lineHeight: 1,
                              flexDirection: 'column',
                              alignContent: 'center',
                              alignItems: 'flex-end',
                            }}
                          >
                            {displayValue}
                          </TradeText>
                        );
                      },
                    },
                    {
                      title: t('margin') + '',
                      dataIndex: 'margin',
                      key: 'margin',
                      align: 'right',
                      width: 150,
                      sorter: (a: any, b: any) =>
                        Number(a?.margin || 0) - Number(b?.margin || 0),
                      render: (text: any, item, data) => {
                        const EP = EPs?.find(
                          (item2: any) => +item2.id == +item?.exchange_pairs_id
                        );
                        const currency = EP?.pairs?.split('/')[1];
                        return (
                          <TradeText
                            style={{
                              letterSpacing: 0,
                              minWidth: 90,
                              lineHeight: 1,
                              flexDirection: 'column',
                              alignContent: 'center',
                              alignItems: 'flex-end',
                              justifyContent: 'flex-end',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                              }}
                            >
                              <KrwPrice
                                price={item?.margin}
                                currency={currency}
                                color={false}
                                equiv={true}
                                dp={3}
                              />
                              {item?.cross_iso === 'I' && (
                                <div
                                  style={{
                                    fontSize: 20,
                                    marginBottom: 6,
                                    color: '#007AFF',
                                    cursor: 'pointer',
                                  }}
                                >
                                  +
                                </div>
                              )}
                            </div>
                          </TradeText>
                        );
                      },
                    },
                    {
                      title: t('margin') + ' ' + t('ratio'),
                      dataIndex: 'margin_ratio',
                      key: 'margin_ratio',
                      align: 'right',
                      sorter: (a: any, b: any) =>
                        marginRatioOf(a) - marginRatioOf(b),
                      render: (text: any, item) => {
                        const EP = EPs?.find(
                          (item2: any) => +item2.id == +item?.exchange_pairs_id
                        );
                        const currency = EP?.pairs?.split('/')[1];

                        // 현재 가격
                        const currentPrice = getPrice(item?.exchange_pairs_id);

                        // 청산 가격
                        const liquidationPrice = BigNumber(
                          item?.liquid_price
                        ).toNumber();

                        // 진입 가격
                        const entryPrice = BigNumber(item?.price).toNumber();

                        // 청산 예상가와 현재 가격 사이의 거리 계산
                        let priceDistance = 0;
                        let priceTotalDistance = 0;

                        if (item?.long_short === 'L') {
                          // 롱 포지션: 진입가 - 청산가 = 총 거리
                          priceTotalDistance = entryPrice - liquidationPrice;
                          // 현재가 - 청산가 = 현재 거리
                          priceDistance = currentPrice - liquidationPrice;
                        } else {
                          // 숏 포지션: 청산가 - 진입가 = 총 거리
                          priceTotalDistance = liquidationPrice - entryPrice;
                          // 청산가 - 현재가 = 현재 거리
                          priceDistance = liquidationPrice - currentPrice;
                        }

                        // 마진 비율 계산: (현재 거리 / 총 거리) * 100
                        // 가격이 청산가에 가까워질수록 비율이 0에 가까워짐
                        let marginRatio =
                          priceTotalDistance > 0
                            ? (priceDistance / priceTotalDistance) * 100
                            : 0;

                        // 마진 비율 반전 처리: 100에서 빼서 청산가에 가까울수록 100%에 가까워지게 함
                        marginRatio = 100 - marginRatio;

                        // 0보다 작으면 0으로, 100보다 크면 100으로 제한
                        marginRatio = Math.max(0, Math.min(100, marginRatio));

                        return (
                          <TradeText
                            style={{
                              letterSpacing: 0,
                              minWidth: 90,
                              lineHeight: 1,
                              flexDirection: 'column',
                              alignItems: 'flex-end',
                              justifyContent: 'flex-end',
                              color:
                                marginRatio > 80
                                  ? COLORS.lightRed
                                  : marginRatio > 50
                                    ? '#007AFF'
                                    : 'inherit',
                            }}
                          >
                            {marginRatio.toFixed(2)}%
                          </TradeText>
                        );
                      },
                    },
                    {
                      title: t('unrealizedplpercent') + '',
                      dataIndex: 'unrealized_pl_percent',
                      key: 'unrealized_pl_percent',
                      width: 180,
                      sorter: (a: any, b: any) =>
                        unrealizedPnlOf(a) - unrealizedPnlOf(b),
                      onHeaderCell: (column) => ({
                        style: {
                          textAlign: 'right',
                          paddingRight: '32px',
                        },
                      }),
                      render: (text: any, item) => {
                        const EP = EPs?.find(
                          (item2: any) => +item2.id == +item?.exchange_pairs_id
                        );
                        const currency = EP?.pairs?.split('/')[1];
                        let pnl = 0;
                        const currentPrice = getPrice(item?.exchange_pairs_id);
                        const amount = item?.amount * item?.leverage;

                        if (item?.long_short === 'L') {
                          pnl = BigNumber(currentPrice)
                            .minus(item?.price) // 진입가
                            .multipliedBy(amount)
                            .toNumber();
                        } else {
                          pnl = BigNumber(item?.price)
                            .minus(currentPrice)
                            .multipliedBy(amount)
                            .toNumber();
                        }

                        // 진입 수수료 계산 - 전체 포지션에 대한 수수료
                        const entryFee = item?.orders?.[0]?.fee_price || 0;

                        // 실현된 청산 수수료 계산
                        let realizedExitFees = 0;
                        if (item?.orders && item.orders.length > 1) {
                          for (let i = 1; i < item.orders.length; i++) {
                            if (item.orders[i]?.fee_price) {
                              realizedExitFees += Number(
                                item.orders[i].fee_price
                              );
                            }
                          }
                        }

                        // 남은 포지션 비율 계산
                        const originalAmount =
                          item?.orders?.[0]?.amount || item?.amount;
                        const remainingRatio = BigNumber(item.amount)
                          .dividedBy(originalAmount)
                          .toNumber();

                        // 남은 진입 수수료 계산 (전체 진입 수수료 * 남은 비율)
                        const remainingEntryFee = BigNumber(entryFee)
                          .multipliedBy(remainingRatio)
                          .toNumber();

                        // 예상 청산 수수료 계산 (남은 진입 수수료와 동일하게 가정)
                        const estimatedExitFee = remainingEntryFee;

                        // 최종 PnL 계산: 미실현 손익 - 남은 진입 수수료 - 예상 청산 수수료
                        const pnlWithFees = BigNumber(pnl)
                          .minus(remainingEntryFee)
                          .minus(estimatedExitFee)
                          .toNumber();

                        return (
                          <View
                            style={{
                              flexDirection: 'row',
                              gap: 0,
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              width: '100%',
                            }}
                          >
                            <View
                              style={{
                                flexDirection: 'row',
                                alignItems: 'center',
                              }}
                            >
                              <KrwPrice
                                price={pnl}
                                equiv={equivshow}
                                ratemargin={formatDecimal4Truncate(
                                  item?.margin
                                )}
                                currency={currency}
                                color={true}
                                dp={4}
                              />
                              <CRenderEmpty id={item?.exchange_pairs_id} />
                            </View>

                            <Button
                              style={{ border: 'none', marginLeft: 'auto' }}
                              ghost
                              size="small"
                              icon={
                                <IconShare2
                                  color={
                                    pnl > 0
                                      ? COLORS.lightGreen
                                      : pnl < 0
                                        ? COLORS.lightRed
                                        : textw
                                  }
                                  size={16}
                                />
                              }
                              onClick={() => {
                                setShareData(null);
                                setTimeout(() => {
                                  setShareData(item);
                                }, 100);
                              }}
                            />
                          </View>
                        );
                      },
                    },
                    {
                      title: t('realizedpl') + '',
                      dataIndex: 'realized_pl',
                      key: 'realized_pl',
                      align: 'right',
                      width: 180,
                      sorter: (a: any, b: any) =>
                        realizedPnlWithFeesOf(a) - realizedPnlWithFeesOf(b),
                      render: (text: any, item) => {
                        const EP = EPs?.find(
                          (item2: any) => +item2.id == +item?.exchange_pairs_id
                        );
                        const currency = EP?.pairs?.split('/')[1];

                        // orders 배열이 시간 역순(최신→과거)이므로 open_close로 명시적 구분 필요
                        const entryOrders =
                          item?.orders?.filter(
                            (o: any) => o.open_close === 'O'
                          ) || [];
                        const closeOrders =
                          item?.orders?.filter(
                            (o: any) => o.open_close === 'C'
                          ) || [];

                        // 진입 수수료: 모든 진입 주문의 수수료 합산 (이미 지불된 비용)
                        const entryFee = entryOrders.reduce(
                          (sum: number, o: any) =>
                            sum + Number(o.fee_price || 0),
                          0
                        );

                        // 청산 수수료: 모든 청산 주문의 수수료 합산
                        const exitFees = closeOrders.reduce(
                          (sum: number, o: any) =>
                            sum + Number(o.fee_price || 0),
                          0
                        );

                        let realizedPnLWithFees = 0;

                        if (closeOrders.length === 0) {
                          // 청산 없음 → 진입 수수료만 실현 (이미 지불됨)
                          realizedPnLWithFees = -entryFee;
                        } else {
                          // 청산 있음 → pnl_price - 전체 진입 수수료 - 청산 수수료
                          // 진입 수수료는 포지션 오픈 시 이미 전액 지불되었으므로 비율 계산 없이 전체 포함
                          realizedPnLWithFees = BigNumber(item.pnl_price || 0)
                            .minus(entryFee)
                            .minus(exitFees)
                            .toNumber();
                        }

                        return (
                          <View
                            style={{
                              display: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'flex-end',
                              width: '100%',
                            }}
                          >
                            <KrwPrice
                              price={formatDecimal4Truncate(
                                realizedPnLWithFees
                              )}
                              currency={currency}
                              equiv={equivshow}
                              dp={4}
                            />
                          </View>
                        );
                      },
                    },
                  ]
                : [
                    {
                      width: '9%',
                      title: (
                        <ColumnHeader
                          title={t('contracts')}
                          columnKey="contracts"
                          sortedInfo={sortedInfo}
                        />
                      ),
                      dataIndex: 'contracts',
                      align: 'left',
                      key: 'contracts',
                      sorter: (a: any, b: any) => {
                        const aPairs = getPairs(a);
                        const bPairs = getPairs(b);
                        return (aPairs || '').localeCompare(bPairs || '');
                      },
                      render: (text: any, item) => (
                        <ContractsColumn item={item} />
                      ),
                    },
                    {
                      width: '9%',
                      title: (
                        <ColumnHeader
                          title={t('qty')}
                          columnKey="amount"
                          sortedInfo={sortedInfo}
                        />
                      ),
                      dataIndex: 'amount',
                      key: 'amount',
                      align: 'left',
                      sorter: (a: any, b: any) => qtyValueOf(a) - qtyValueOf(b),
                      render: (text: any, item: any) => {
                        const EP = EPs?.find(
                          (item2: any) => +item2.id == +item?.exchange_pairs_id
                        );
                        const coin = EP?.pairs?.split('/')[0];
                        const currentPrice = getPrice(item?.exchange_pairs_id);
                        const isLong = item?.long_short === 'L';
                        return (
                          <TradeText
                            style={{
                              letterSpacing: 0,
                              minWidth: 90,
                              whiteSpace: 'nowrap',
                              color: '#fff',
                              fontFamily: 'NanumSquareRound',
                              fontWeight: 700,
                              fontSize: 13,
                              lineHeight: 'normal',
                            }}
                          >
                            {orderType === 'M'
                              ? BigNumber(item?.amount)
                                  .multipliedBy(item?.leverage)
                                  .toFormat(4)
                              : BigNumber(item?.amount)
                                  .multipliedBy(currentPrice)
                                  .multipliedBy(item?.leverage)
                                  .toFormat(4)}{' '}
                            {orderType === 'M' ? coin : 'USDT'}
                          </TradeText>
                        );
                      },
                    },
                    {
                      width: '9%',
                      title: (
                        <ColumnHeader
                          title={t('entryPrice')}
                          columnKey="open_price"
                          sortedInfo={sortedInfo}
                        />
                      ),
                      dataIndex: 'open_price',
                      key: 'open_price',
                      align: 'left',
                      sorter: (a: any, b: any) =>
                        entryPriceOf(a) - entryPriceOf(b),
                      render: (text: any, item) => {
                        const EP = EPs?.find(
                          (item2: any) => +item2.id == +item?.exchange_pairs_id
                        );

                        return (
                          <TradeText
                            style={{
                              letterSpacing: 0,
                              minWidth: 90,
                              whiteSpace: 'nowrap',
                              height: 17,
                              alignItems: 'flex-end',
                              color: '#fff',
                              fontFamily: 'NanumSquareRound',
                              fontSize: 12,
                            }}
                          >
                            {isPositionLoading && !item?.price ? (
                              <Skeleton.Input
                                active
                                size="small"
                                style={{ width: 60, height: 14 }}
                              />
                            ) : item?.price ? (
                              BigNumber(item.price).toFormat(EP?.dp || 3)
                            ) : (
                              '--'
                            )}
                          </TradeText>
                        );
                      },
                    },
                    {
                      width: '9%',
                      title: (
                        <ColumnHeader
                          title={t('markPrice')}
                          columnKey="marketAveragePrice"
                          sortedInfo={sortedInfo}
                        />
                      ),
                      dataIndex: 'marketAveragePrice',
                      key: 'marketAveragePrice',
                      align: 'left',
                      sorter: (a: any, b: any) =>
                        currentPriceOf(a) - currentPriceOf(b),
                      render: (text: any, item) => {
                        const ep = EPs?.find(
                          (ep) => +ep.id === +item.exchange_pairs_id
                        );
                        const symbol = ep?.pairs
                          ?.replace('/', '')
                          .toLowerCase();
                        const price =
                          useMultiMarkPriceStore.getState().prices[symbol] || 0;

                        return (
                          <TradeText
                            style={{
                              letterSpacing: 0,
                              minWidth: 90,
                              whiteSpace: 'nowrap',
                              height: 17,
                              alignItems: 'flex-end',
                              color: '#fff',
                              fontFamily: 'NanumSquareRound',
                              fontSize: 12,
                            }}
                          >
                            {/*{BigNumber(*/}
                            {/*  getPrice(item?.exchange_pairs_id)*/}
                            {/*).toFormat(EP?.dp || 3)}*/}
                            {BigNumber(price).toFormat(ep?.dp || 3)}
                          </TradeText>
                        );
                      },
                    },
                    {
                      width: '9%',
                      title: (
                        <ColumnHeader
                          title={t('liqPrice')}
                          columnKey="liquidation_price"
                          sortedInfo={sortedInfo}
                        />
                      ),
                      dataIndex: 'liquidation_price',
                      key: 'liquidation_price',
                      align: 'left',
                      sorter: (a: any, b: any) => liqPriceOf(a) - liqPriceOf(b),
                      render: (text: any, item) => {
                        const EP = EPs?.find(
                          (item2: any) => +item2.id == +item?.exchange_pairs_id
                        );
                        const liquidPrice = BigNumber(item?.liquid_price);
                        const displayValue = liquidPrice.isNegative()
                          ? '--'
                          : liquidPrice.toFormat(EP?.dp || 3);

                        if (liquidPrice.isNegative()) {
                          return (
                            <Tooltip title={t('positionRiskLowTooltip')}>
                              <TradeText
                                style={{
                                  letterSpacing: 0,
                                  minWidth: 90,
                                  whiteSpace: 'nowrap',
                                  height: 17,
                                  alignItems: 'flex-end',
                                  fontFamily: 'NanumSquareRound',
                                  fontSize: 12,
                                  color: COLORS.lightRed,
                                }}
                              >
                                {displayValue}
                              </TradeText>
                            </Tooltip>
                          );
                        }

                        return (
                          <TradeText
                            style={{
                              letterSpacing: 0,
                              minWidth: 90,
                              whiteSpace: 'nowrap',
                              height: 17,
                              alignItems: 'flex-end',
                              fontFamily: 'NanumSquareRound',
                              fontSize: 12,
                              color: COLORS.lightRed,
                            }}
                          >
                            {displayValue}
                          </TradeText>
                        );
                      },
                    },
                    {
                      width: '9%',
                      title: (
                        <ColumnHeader
                          title={t('margin')}
                          columnKey="margin"
                          sortedInfo={sortedInfo}
                        />
                      ),
                      dataIndex: 'margin',
                      key: 'margin',
                      align: 'left',
                      sorter: (a: any, b: any) =>
                        Number(a?.margin || 0) - Number(b?.margin || 0),
                      render: (text: any, item) => {
                        const EP = EPs?.find(
                          (item2: any) => +item2.id == +item?.exchange_pairs_id
                        );
                        const currency = EP?.pairs?.split('/')[1];
                        return (
                          <View
                            style={{ minWidth: 90, alignItems: 'flex-start' }}
                          >
                            <KrwPrice
                              price={item?.margin}
                              currency={currency}
                              color={false}
                              equiv={true}
                              dp={2}
                              align="flex-start"
                            />
                          </View>
                        );
                      },
                    },
                    {
                      width: '9%',
                      title: (
                        <ColumnHeader
                          title={t('margin') + ' ' + t('ratio')}
                          columnKey="margin_ratio"
                          sortedInfo={sortedInfo}
                        />
                      ),
                      dataIndex: 'margin_ratio',
                      key: 'margin_ratio',
                      align: 'left',
                      sorter: (a: any, b: any) =>
                        marginRatioOf(a) - marginRatioOf(b),
                      render: (text: any, item) => {
                        // const EP = EPs?.find(
                        //   (item2: any) => +item2.id == +item?.exchange_pairs_id
                        // );
                        // const currency = EP?.pairs?.split('/')[1];

                        // // 현재 가격
                        // const currentPrice = getPrice(item?.exchange_pairs_id);

                        // // 청산 가격
                        // const liquidationPrice = BigNumber(item?.liquid_price).toNumber();

                        // // 진입 가격
                        // const entryPrice = BigNumber(item?.price).toNumber();

                        // // 청산 예상가와 현재 가격 사이의 거리 계산
                        // let priceDistance = 0;
                        // let priceTotalDistance = 0;

                        // if (item?.long_short === 'L') {
                        //   // 롱 포지션: 진입가 - 청산가 = 총 거리
                        //   priceTotalDistance = entryPrice - liquidationPrice;
                        //   // 현재가 - 청산가 = 현재 거리
                        //   priceDistance = currentPrice - liquidationPrice;
                        // } else {
                        //   // 숏 포지션: 청산가 - 진입가 = 총 거리
                        //   priceTotalDistance = liquidationPrice - entryPrice;
                        //   // 청산가 - 현재가 = 현재 거리
                        //   priceDistance = liquidationPrice - currentPrice;
                        // }

                        // // 마진 비율 계산: (현재 거리 / 총 거리) * 100
                        // // 가격이 청산가에 가까워질수록 비율이 0에 가까워짐
                        // let marginRatio = priceTotalDistance > 0
                        //   ? (priceDistance / priceTotalDistance) * 100
                        //   : 0;

                        // // 마진 비율 반전 처리: 100에서 빼서 청산가에 가까울수록 100%에 가까워지게 함
                        // marginRatio = 100 - marginRatio;

                        // // 0보다 작으면 0으로, 100보다 크면 100으로 제한
                        // marginRatio = Math.max(0, Math.min(100, marginRatio));

                        return <TradeMarginRatio item={item} />;
                      },
                    },
                    {
                      width: '12%',
                      title: (
                        <ColumnHeader
                          title={t('unrealizedplpercent')}
                          columnKey="unrealized_pl_percent"
                          sortedInfo={sortedInfo}
                        />
                      ),
                      dataIndex: 'unrealized_pl_percent',
                      key: 'unrealized_pl_percent',
                      align: 'left',
                      sorter: (a: any, b: any) =>
                        unrealizedPnlOf(a) - unrealizedPnlOf(b),
                      render: (text: any, item) => {
                        const EP = EPs?.find(
                          (item2: any) => +item2.id == +item?.exchange_pairs_id
                        );
                        const currency = EP?.pairs?.split('/')[1];
                        let pnl = 0;
                        const currentPrice = getPrice(item?.exchange_pairs_id);
                        const amount = item?.amount * item?.leverage;

                        if (item?.long_short === 'L') {
                          pnl = BigNumber(currentPrice)
                            .minus(item?.price) // 진입가
                            .multipliedBy(amount)
                            .toNumber();
                        } else {
                          pnl = BigNumber(item?.price)
                            .minus(currentPrice)
                            .multipliedBy(amount)
                            .toNumber();
                        }

                        // 진입 수수료 계산 - 전체 포지션에 대한 수수료
                        const entryFee = item?.orders?.[0]?.fee_price || 0;

                        // 실현된 청산 수수료 계산
                        let realizedExitFees = 0;
                        if (item?.orders && item.orders.length > 1) {
                          for (let i = 1; i < item.orders.length; i++) {
                            if (item.orders[i]?.fee_price) {
                              realizedExitFees += Number(
                                item.orders[i].fee_price
                              );
                            }
                          }
                        }

                        // 남은 포지션 비율 계산
                        const originalAmount =
                          item?.orders?.[0]?.amount || item?.amount;
                        const remainingRatio = BigNumber(item.amount)
                          .dividedBy(originalAmount)
                          .toNumber();

                        // 남은 진입 수수료 계산 (전체 진입 수수료 * 남은 비율)
                        const remainingEntryFee = BigNumber(entryFee)
                          .multipliedBy(remainingRatio)
                          .toNumber();

                        // 예상 청산 수수료 계산 (남은 진입 수수료와 동일하게 가정)
                        const estimatedExitFee = remainingEntryFee;

                        // 최종 PnL 계산: 미실현 손익 - 남은 진입 수수료 - 예상 청산 수수료
                        const pnlWithFees = BigNumber(pnl)
                          .minus(remainingEntryFee)
                          .minus(estimatedExitFee)
                          .toNumber();

                        return (
                          <View
                            style={{
                              flexDirection: 'row',
                              gap: 4,
                              justifyContent: 'flex-start',
                              alignItems: 'center',
                            }}
                          >
                            <View
                              style={{
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                minWidth: 80,
                              }}
                            >
                              <KrwPrice
                                price={pnl}
                                equiv={true}
                                ratemargin={formatDecimal4Truncate(
                                  item?.margin
                                )}
                                currency={currency}
                                color={true}
                                dp={2}
                                align="flex-start"
                              />
                              <CRenderEmpty id={item?.exchange_pairs_id} />
                            </View>

                            <Button
                              style={{ border: 'none' }}
                              ghost
                              size="small"
                              icon={
                                <HiOutlineShare color={'#B3B5BD'} size={16} />
                              }
                              onClick={() => {
                                setShareData(null);
                                setTimeout(() => {
                                  setShareData(item);
                                }, 100);
                              }}
                            />
                          </View>
                        );
                      },
                    },
                    {
                      width: '9%',
                      title: (
                        <ColumnHeader
                          title={t('realizedpl')}
                          columnKey="realized_pl"
                          sortedInfo={sortedInfo}
                        />
                      ),
                      dataIndex: 'realized_pl',
                      key: 'realized_pl',
                      align: 'left',
                      sorter: (a: any, b: any) =>
                        realizedPnlWithFeesOf(a) - realizedPnlWithFeesOf(b),
                      render: (text: any, item) => {
                        const EP = EPs?.find(
                          (item2: any) => +item2.id == +item?.exchange_pairs_id
                        );
                        const currency = EP?.pairs?.split('/')[1];

                        // orders 배열이 시간 역순(최신→과거)이므로 open_close로 명시적 구분 필요
                        const entryOrders =
                          item?.orders?.filter(
                            (o: any) => o.open_close === 'O'
                          ) || [];
                        const closeOrders =
                          item?.orders?.filter(
                            (o: any) => o.open_close === 'C'
                          ) || [];

                        // 진입 수수료: 모든 진입 주문의 수수료 합산 (이미 지불된 비용)
                        const entryFee = entryOrders.reduce(
                          (sum: number, o: any) =>
                            sum + Number(o.fee_price || 0),
                          0
                        );

                        // 청산 수수료: 모든 청산 주문의 수수료 합산
                        const exitFees = closeOrders.reduce(
                          (sum: number, o: any) =>
                            sum + Number(o.fee_price || 0),
                          0
                        );

                        let realizedPnLWithFees = 0;

                        if (closeOrders.length === 0) {
                          // 청산 없음 → 진입 수수료만 실현 (이미 지불됨)
                          realizedPnLWithFees = -entryFee;
                        } else {
                          // 청산 있음 → pnl_price - 전체 진입 수수료 - 청산 수수료
                          // 진입 수수료는 포지션 오픈 시 이미 전액 지불되었으므로 비율 계산 없이 전체 포함
                          realizedPnLWithFees = BigNumber(item.pnl_price || 0)
                            .minus(entryFee)
                            .minus(exitFees)
                            .toNumber();
                        }

                        return (
                          <View
                            style={{ minWidth: 90, alignItems: 'flex-start' }}
                          >
                            <KrwPrice
                              price={formatDecimal4Truncate(
                                realizedPnLWithFees
                              )}
                              equiv={true}
                              dp={2}
                              currency={currency}
                            />
                          </View>
                        );
                      },
                    },
                    {
                      width: '9%',
                      title: (
                        <ColumnHeader
                          title={t('closeBy')}
                          hasSorter={false}
                          sortedInfo={sortedInfo}
                        />
                      ),
                      dataIndex: 'close_by',
                      key: 'close_by',
                      align: 'left',
                      sorter: false,
                      render: (text: any, item) => {
                        return (
                          <>
                            <Closedby
                              item={item}
                              closeAll={closed}
                              setCloseAll={toggleClosed}
                              refresh={item?.exchange_pairs_id}
                              test={'5'}
                              orderBookValue={orderBookValue}
                              isMobile={isMobile}
                            />
                          </>
                        );
                      },
                    },
                    {
                      width: '9%',
                      title: (
                        <ColumnHeader
                          title={t('TP/SL')}
                          hasSorter={false}
                          sortedInfo={sortedInfo}
                        />
                      ),
                      dataIndex: 'tp/sl',
                      key: 'tp/sl',
                      align: 'left',
                      sorter: false,
                      render: (text: any, item) => {
                        return (
                          <TPSLEDIT
                            posid={item?.id}
                            tpsl={item?.tpsl?.[0]}
                            price={item?.price}
                            amount={item?.amount}
                            long_short={item?.long_short}
                            leverage={item?.leverage}
                            margin={item?.margin}
                            mark={getPrice(item?.exchange_pairs_id)}
                            liq={item?.liquid_price}
                            exchange_pairs_id={item?.exchange_pairs_id}
                            isMobile={isMobile}
                          />
                        );
                      },
                    },
                  ]
            }
            pagination={false}
            style={{ width: '100%', height: '100%' }}
            sticky={true}
            scroll={
              needsScroll
                ? {
                    x: 'max-content',
                    y: isMobile
                      ? '100%'
                      : parentHeight - 20 - (headerHeight || 38),
                  }
                : { x: 'max-content' }
            }
            size="small"
          />
        </StyledTableContainer>
      ) : (
        <EmptyPosition type="position" />
      )}
      <ShareModal
        shareData={shareData}
        setShareData={setShareData}
        isMobile={isMobile}
      />
    </TradeDetailListSection>
  );
};

export default React.memo(TradePositionsTable);

const StyledTableContainer = styled.div`
  width: 100%;
  height: 100%;

  .ant-table-wrapper {
    .ant-table {
      .ant-table-container {
        .ant-table-body {
          overflow: auto !important;

          /* 스크롤바 커스터마이징 */
          &::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }

          &::-webkit-scrollbar-track {
            background: transparent;
          }

          &::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.3);
            border-radius: 4px;

            &:hover {
              background: rgba(255, 255, 255, 0.5);
            }
          }

          /* Firefox 스크롤바 스타일링 */
          scrollbar-width: thin;
          scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
        }
      }
    }
  }
`;

const PlusButton = styled.div`
  font-size: 20px;
  margin-bottom: 6px;
  color: #007aff;
  cursor: pointer;
  &:hover {
    color: #ff9b70;
  }
`;
